import { useContext, useEffect } from "react"
import { Button, Typography } from "@mui/material"
import { UNSAFE_DataRouterContext, useBlocker } from "react-router-dom"
import { Modal } from "@core/components/modal"

export const templatesPath = "/telegram/post-bot/templates"
export const templatePath = (id: string) =>
  `${templatesPath}/${encodeURIComponent(id)}`
export const templateVersionPath = (id: string) =>
  `/telegram/post-bot/template-versions/${encodeURIComponent(id)}`
export const isTemplateId = (id: unknown): id is string =>
  typeof id === "string" &&
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id)
export const newTemplateVersionPath = (
  templateId: string,
  sourceVersionId?: string,
) =>
  `${templatePath(templateId)}/versions/new${sourceVersionId ? `?sourceVersionId=${encodeURIComponent(sourceVersionId)}` : ""}`

/** Task13 handoff: IDs only. A return is an explicit offer to a publication's
 * local draft, NEVER a publication PATCH or automatic version switch.
 * Task13 owns retaining that draft while the author is away.
 */
export interface TemplateReturnContext {
  returnToPublicationId?: string
  sourceTemplateId?: string
  sourceTemplateVersionId?: string
}
export interface SavedTemplateReturnContext extends TemplateReturnContext {
  savedVersionId: string
}
export function readTemplateReturnContext(
  state: unknown,
): TemplateReturnContext {
  if (!state || typeof state !== "object") return {}
  return Object.fromEntries(
    [
      "returnToPublicationId",
      "sourceTemplateId",
      "sourceTemplateVersionId",
    ].flatMap((key) => {
      const value = (state as Record<string, unknown>)[key]
      return typeof value === "string" &&
        /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
          value,
        )
        ? [[key, value]]
        : []
    }),
  )
}

const warning = "Уйти без сохранения черновика?"
export const confirmDiscardVersionDraft = (dirty: boolean) =>
  !dirty || window.confirm(warning)

function DataRouterGuard({ dirty }: { dirty: boolean }) {
  // Media pagination keeps the draft; changing the clone source starts a new session.
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      dirty &&
      (currentLocation.pathname !== nextLocation.pathname ||
        new URLSearchParams(currentLocation.search).get("sourceVersionId") !==
          new URLSearchParams(nextLocation.search).get("sourceVersionId")),
  )
  if (blocker.state !== "blocked") return null
  return (
    <Modal
      title="Несохранённая версия"
      onClose={() => blocker.reset()}
      actions={[
        <Button key="stay" onClick={() => blocker.reset()}>
          Остаться
        </Button>,
        <Button key="leave" color="warning" onClick={() => blocker.proceed()}>
          Уйти без сохранения
        </Button>,
      ]}
    >
      <Typography>{warning}</Typography>
    </Modal>
  )
}

/** The app currently uses createBrowserRouter. Embedded BrowserRouter/MemoryRouter
 * hosts must NOT call useBlocker: use beforeunload + confirmed same-tab links.
 * No history monkey-patch; non-data-router programmatic/back navigation belongs
 * to that host and should call confirmDiscardVersionDraft before discarding.
 */
export function VersionDraftNavigationGuard({ dirty }: { dirty: boolean }) {
  const dataRouter = useContext(UNSAFE_DataRouterContext)
  useEffect(() => {
    if (!dirty) return
    const unload = (event: BeforeUnloadEvent) => {
      event.preventDefault()
      event.returnValue = ""
    }
    const click = (event: MouseEvent) => {
      if (
        dataRouter ||
        event.defaultPrevented ||
        event.button !== 0 ||
        event.ctrlKey ||
        event.metaKey ||
        event.altKey ||
        event.shiftKey
      )
        return
      const link =
        event.target instanceof Element ? event.target.closest("a[href]") : null
      if (
        !(link instanceof HTMLAnchorElement) ||
        link.target === "_blank" ||
        link.hasAttribute("download")
      )
        return
      const next = new URL(link.href)
      if (
        next.origin !== window.location.origin ||
        !confirmDiscardVersionDraft(true)
      ) {
        // External same-tab links also discard the draft; beforeunload handles them.
        if (next.origin !== window.location.origin) return
        event.preventDefault()
        event.stopPropagation()
      }
    }
    window.addEventListener("beforeunload", unload)
    document.addEventListener("click", click, true)
    return () => {
      window.removeEventListener("beforeunload", unload)
      document.removeEventListener("click", click, true)
    }
  }, [dirty, dataRouter])
  return dataRouter ? <DataRouterGuard dirty={dirty} /> : null
}
